import React, { useState, useEffect } from 'react';
import {
  CreditCardIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const StripePaymentPage = ({ paymentToken }) => {
  const [loading, setLoading] = useState(true);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadPaymentInfo();
    // Check URL for return status
    const params = new URLSearchParams(window.location.search);
    const status = params.get('status');
    if (status === 'success') {
      setPaymentInfo({ status: 'paid', message: 'Payment successful! Thank you. A receipt has been sent to your email.' });
      setLoading(false);
    }
  }, [paymentToken]);

  const loadPaymentInfo = async () => {
    try {
      const response = await fetch(`/api/stripe/pay/${paymentToken}`);
      const data = await response.json();

      if (response.ok) {
        setPaymentInfo(data);
      } else {
        setError(data.error || 'Payment link not found');
      }
    } catch (err) {
      setError('Failed to load payment information');
    } finally {
      setLoading(false);
    }
  };

  const handlePayNow = async () => {
    setProcessing(true);
    setError('');

    try {
      const response = await fetch(`/api/stripe/pay/${paymentToken}/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (response.ok && data.checkoutUrl) {
        // Redirect to Stripe Checkout
        window.location.href = data.checkoutUrl;
      } else {
        setError(data.error || 'Failed to start payment');
        setProcessing(false);
      }
    } catch (err) {
      setError('Failed to connect to payment service');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading payment details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <XCircleIcon className="h-16 w-16 text-red-400 mx-auto" />
          <h1 className="mt-4 text-xl font-semibold text-gray-900">Payment Not Available</h1>
          <p className="mt-2 text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // Already paid
  if (paymentInfo?.status === 'paid') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto" />
          <h1 className="mt-4 text-xl font-semibold text-gray-900">Payment Complete</h1>
          <p className="mt-2 text-gray-600">{paymentInfo.message || 'This invoice has already been paid. Thank you!'}</p>
        </div>
      </div>
    );
  }

  // Expired or cancelled
  if (paymentInfo?.status === 'expired' || paymentInfo?.status === 'cancelled') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <ExclamationTriangleIcon className="h-16 w-16 text-yellow-400 mx-auto" />
          <h1 className="mt-4 text-xl font-semibold text-gray-900">Link Expired</h1>
          <p className="mt-2 text-gray-600">{paymentInfo.message}</p>
        </div>
      </div>
    );
  }

  // Payment form
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-indigo-600 px-6 py-4 text-white">
          <div className="flex items-center gap-3">
            <CreditCardIcon className="h-8 w-8" />
            <div>
              <h1 className="text-lg font-semibold">{paymentInfo?.siteName || 'Payment'}</h1>
              <p className="text-indigo-200 text-sm">Invoice #{paymentInfo?.saleId}</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Contact */}
          {paymentInfo?.contactName && (
            <div>
              <p className="text-sm text-gray-500">Bill To</p>
              <p className="font-medium text-gray-900">{paymentInfo.contactName}</p>
            </div>
          )}

          {/* Items */}
          {paymentInfo?.items?.length > 0 && (
            <div>
              <p className="text-sm text-gray-500 mb-2">Items</p>
              <div className="border rounded-md divide-y divide-gray-200">
                {paymentInfo.items.map((item, index) => (
                  <div key={index} className="flex items-center justify-between px-4 py-3">
                    <div>
                      <span className="text-sm font-medium text-gray-900">{item.name}</span>
                      <span className="text-sm text-gray-500"> x {item.qty}</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      ${(item.price * item.qty).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Amount Due */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-lg font-semibold text-gray-900">Amount Due</span>
              <span className="text-2xl font-bold text-indigo-600">
                ${paymentInfo?.amount?.toFixed(2)}
              </span>
            </div>
          </div>

          {/* Pay Button */}
          <button
            onClick={handlePayNow}
            disabled={processing}
            className="w-full rounded-md bg-green-600 px-4 py-4 text-lg font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 disabled:opacity-50 transition-colors"
          >
            {processing ? (
              <span className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Connecting to Stripe...
              </span>
            ) : (
              `Pay $${paymentInfo?.amount?.toFixed(2)} Now`
            )}
          </button>

          {/* Security Badge */}
          <div className="flex items-center justify-center gap-2 text-gray-400 text-xs">
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            <span>Secured by Stripe - Your payment information is encrypted</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StripePaymentPage;
